import React, { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { pageServices } from "../../APIServices/SitePilotManagement/pageServices";
import {
  FaBehance,
  FaDiscord,
  FaDribbble,
  FaFacebook,
  FaFacebookMessenger,
  FaFigma,
  FaGithub,
  FaGitlab,
  FaInstagram,
  FaLine,
  FaLinkedin,
  FaPinterest,
  FaQuora,
  FaReddit,
  FaShopify,
  FaSkype,
  FaSlack,
  FaSnapchat,
  FaSoundcloud,
  FaSpotify,
  FaStackOverflow,
  FaTelegram,
  FaThreads,
  FaTumblr,
  FaTwitter,
  FaViber,
  FaWeixin,
  FaWhatsapp,
  FaWordpress,
  FaXTwitter,
  FaYoutube,
} from "react-icons/fa6";
import NotFoundPage from "../NotFoundPage";
import BlogSection from "./Blog/BlogSection";
import HeroSection from "./Sections/HeroSection";
import AboutSection from "./Sections/AboutSection";
import HeaderSection from "./Sections/HeaderSection";
import FooterSection from "./Sections/FooterSection";
import CustomSection from "./Sections/CustomSection";
import ChatFloatIcon from "../ChatWidget/ChatFloatIcon";
import ServicesSection from "./Sections/ServicesSection";
import ExpertiseSection from "./Sections/ExpertiseSection";
import SuperkartThemePageRoute from "./MarketTemplate/SuperkartThemePageRoute";

const WIDGET_NOTIFY_EVENT = "sitepilot:widget-notify";
const ADMIN_SOCKET_ID = "admin";
const ADMIN_SOCKET_NAME = "Admin";

const ICON_MAP = {
  Whatsapp: FaWhatsapp,
  Instagram: FaInstagram,
  Facebook: FaFacebook,
  Messenger: FaFacebookMessenger,
  Linkedin: FaLinkedin,
  Twitter: FaTwitter,
  X: FaXTwitter,
  Youtube: FaYoutube,
  Snapchat: FaSnapchat,
  Telegram: FaTelegram,
  Pinterest: FaPinterest,
  Reddit: FaReddit,
  Discord: FaDiscord,
  Slack: FaSlack,
  Tumblr: FaTumblr,
  Viber: FaViber,
  Wechat: FaWeixin,
  Line: FaLine,
  Skype: FaSkype,
  Quora: FaQuora,
  Threads: FaThreads,
  Github: FaGithub,
  Gitlab: FaGitlab,
  Stackoverflow: FaStackOverflow,
  Dribbble: FaDribbble,
  Behance: FaBehance,
  Figma: FaFigma,
  Spotify: FaSpotify,
  Soundcloud: FaSoundcloud,
  Wordpress: FaWordpress,
  Shopify: FaShopify,
};

const CustomerLandingPage = ({ pageId }) => {
  const { id } = useParams();
  const pageUniqueId = pageId || id;

  const [activeSection, setActiveSection] = useState("home");
  const [dataAvailable, setDataAvailable] = useState(false);
  const [loader, setLoader] = useState(false);
  const [tabsManage, setTabsManage] = useState([]);
  const [showChat, setShowChat] = useState(false);
  const [pageDetails, setPageDetails] = useState("");
  const [widgetId, setWidgetId] = useState("");

  const getPageDetailById = async (pid) => {
    if (!pid) return;

    setLoader(true);

    try {
      const data = await pageServices.getPageDetailById(pid);

      setWidgetId(data?.widgetId || "");
      setDataAvailable(!!data?.data);
      setPageDetails(data?.data?.pageSettings || "");

      const managedTabs = data?.data?.pageSettings?.headerSection?.tabsManage || [];

      if (managedTabs.length > 0) {
        setTabsManage(managedTabs);
      } else {
        setTabsManage([
          {
            isShow: data?.data?.pageSettings?.aboutSection?.aboutSection || false,
            id: "about",
            name: data?.data?.pageSettings?.aboutSection?.aboutTitle || "About",
          },
          {
            isShow:
              data?.data?.pageSettings?.customDataSection?.customDataSection || false,
            id: "custom",
            name:
              data?.data?.pageSettings?.customDataSection?.customDataSectionTitle ||
              "Custom",
          },
          {
            isShow:
              data?.data?.pageSettings?.expertiseSection?.expertiseSection || false,
            id: "expertise",
            name:
              data?.data?.pageSettings?.expertiseSection?.expertiseSectionTitle ||
              "Expertise",
          },
          {
            isShow:
              data?.data?.pageSettings?.servicesSection?.servicesSection || false,
            id: "services",
            name:
              data?.data?.pageSettings?.servicesSection?.servicesSectionTitle ||
              "Services",
          },
          {
            isShow: data?.data?.pageSettings?.headerSection?.blogMenu || false,
            id: "blog",
            name: "Blog",
          },
          {
            isShow: data?.data?.pageSettings?.headerSection?.storeMenu || false,
            id: "store",
            name: "Store",
          },
        ]);
      }
    } catch (error) {
      console.error("Failed to load page details:", error);
      setDataAvailable(false);
      setPageDetails("");
      setTabsManage([]);
      setWidgetId("");
    } finally {
      setLoader(false);
    }
  };

  useEffect(() => {
    if (!pageUniqueId) return;
    getPageDetailById(pageUniqueId);
  }, [pageUniqueId]);

  const scrollToSection = (secId) => {
    document.getElementById(secId)?.scrollIntoView({ behavior: "smooth" });
  };

  const sendNotification = useCallback(
    (type, text) => {
      if (!widgetId) {
        console.warn("Widget notification skipped because widgetId is missing", {
          type,
          text,
        });
        return;
      }

      window.dispatchEvent(
        new CustomEvent(WIDGET_NOTIFY_EVENT, {
          detail: {
            widgetId,
            type,
            text,
          },
        })
      );
    },
    [widgetId]
  );

  return (
    <>
      <ChatFloatIcon
        widgetId={widgetId}
        widgetType="internal"
        adminUserId={ADMIN_SOCKET_ID}
        adminName={ADMIN_SOCKET_NAME}
      />

      {dataAvailable ? (
        <>
          <HeaderSection
            tabsManage={tabsManage}
            headerSection={pageDetails?.headerSection}
            themeColor={pageDetails?.headerSection?.themeColor}
            scrollToSection={scrollToSection}
            setActiveSection={setActiveSection}
            activeSection={activeSection}
            sendNotification={sendNotification}
          />

          {activeSection === "blog" ? (
            <BlogSection
              servicesSection={pageDetails?.servicesSection}
              themeColor={pageDetails?.headerSection?.themeColor}
              sendNotification={sendNotification}
            />
          ) : activeSection === "store" ? (
            <SuperkartThemePageRoute
              servicesSection={pageDetails?.servicesSection}
              themeColor={pageDetails?.headerSection?.themeColor}
              sendNotification={sendNotification}
            />
          ) : (
            <>
              <HeroSection
                showChat={showChat}
                setShowChat={setShowChat}
                heroSection={pageDetails?.heroSection}
                themeColor={pageDetails?.headerSection?.themeColor}
                scrollToSection={scrollToSection}
                sendNotification={sendNotification}
              />

              {tabsManage?.map((section) => (
                <React.Fragment key={section?.id}>
                  {pageDetails?.aboutSection?.aboutSection &&
                    section?.id === "about" && (
                      <AboutSection
                        aboutSection={pageDetails?.aboutSection}
                        menuId={section?.id}
                        themeColor={pageDetails?.headerSection?.themeColor}
                      />
                    )}

                  {pageDetails?.customDataSection?.customDataSection &&
                    section?.id === "custom" && (
                      <CustomSection
                        customDataSection={pageDetails?.customDataSection}
                        menuId={section?.id}
                        themeColor={pageDetails?.headerSection?.themeColor}
                      />
                    )}

                  {pageDetails?.expertiseSection?.expertiseSection &&
                    section?.id === "expertise" && (
                      <ExpertiseSection
                        expertiseSection={pageDetails?.expertiseSection}
                        menuId={section?.id}
                        themeColor={pageDetails?.headerSection?.themeColor}
                      />
                    )}

                  {pageDetails?.servicesSection?.servicesSection &&
                    section?.id === "services" && (
                      <ServicesSection
                        servicesSection={pageDetails?.servicesSection}
                        menuId={section?.id}
                        themeColor={pageDetails?.headerSection?.themeColor}
                        sendNotification={sendNotification}
                      />
                    )}
                </React.Fragment>
              ))}
            </>
          )}

          {pageDetails?.footerSection?.footerSection && (
            <FooterSection
              footerSection={pageDetails?.footerSection}
              headerSection={pageDetails?.headerSection}
              ICON_MAP={ICON_MAP}
              sendNotification={sendNotification}
            />
          )}
        </>
      ) : (
        !loader && <NotFoundPage />
      )}
    </>
  );
};

export default CustomerLandingPage;
